import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect } from "react";
import { Layout, Dropdown, Avatar, Badge } from "antd";

import {
  UserOutlined,
  FilterOutlined,
} from "@ant-design/icons";

import NotificationBell from "./NotificationBell";
import AdvancedFiltersPanel from "./AdvancedFiltersPanel";
import apiClient from "@/utils/apiClient";

const { Header } = Layout;
const AdminHeader = () => {

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [results, setResults] = useState({
    trips: [],
    leads: [],
  });

  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let count = 0;
    params.forEach((val, key) => {
      if (val && key !== "search") count++;
    });
    setActiveFiltersCount(count);
  }, [location.search]);

  const handleApplyHeaderFilters = (filters) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      const val = filters[key];
      if (Array.isArray(val) && val.length > 0) {
        params.set(key, val.join(","));
      } else if (val !== null && val !== undefined && val !== "") {
        if (typeof val === "object" && val.format) {
          params.set(key, val.format("YYYY-MM-DD"));
        } else if (Array.isArray(val) && val.length === 2 && val[0]?.format) {
          params.set(`${key}From`, val[0].format("YYYY-MM-DD"));
          params.set(`${key}To`, val[1].format("YYYY-MM-DD"));
        } else {
          params.set(key, String(val));
        }
      }
    });

    const queryString = params.toString();
    navigate(queryString ? `/leads?${queryString}` : "/leads");
    setShowAdvancedFilters(false);
  };

  const handleResetHeaderFilters = () => {
    setActiveFiltersCount(0);
    navigate("/leads");
    setShowAdvancedFilters(false);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth < 1024);
  };

  window.addEventListener("resize", handleResize);

  return () => {
    window.removeEventListener("resize", handleResize);
  };
}, []);

  // 🔍 Highlight match
  const highlightMatch = (text, query) => {
    if (!text || !query) return text;

    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={i} style={{ background: "#ffe58f" }}>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };


useEffect(() => {
  const timer = setTimeout(async () => {

    const query = searchQuery.trim();

    if (query.length <= 2) {
      setResults({
        trips: [],
        leads: [],
      });

      setShowDropdown(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

const response = await apiClient.get(
  "/leads",
  {
    params: {
      search: query,
    },
  }
);

const data = response.data;

const records = Array.isArray(data)
  ? data
  : Array.isArray(data?.data)
    ? data.data
    : [];

      setResults({
        trips: records,
        leads: records,
      });

      setActiveIndex(-1);
      setShowDropdown(records.length > 0);

    } catch (err) {
      console.error(err);

      setResults({
        trips: [],
        leads: [],
      });

      setShowDropdown(false);

    } finally {
      setLoading(false);
    }

  }, 300);

  return () => clearTimeout(timer);

}, [searchQuery]);

  const flatResults = [
    ...results.trips.map((t) => ({ ...t, type: "trip" })),
    ...results.leads.map((l) => ({ ...l, type: "lead" })),
  ];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showDropdown || flatResults.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % flatResults.length);
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) =>
          prev <= 0 ? flatResults.length - 1 : prev - 1,
        );
      }

      if (e.key === "Enter" && activeIndex >= 0) {
        const item = flatResults[activeIndex];

        navigate(`/trips/${item._id}`);

        setShowDropdown(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [flatResults, activeIndex, showDropdown]);

const handleLogout = () => {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("companyId");

    if (logout) logout();

    window.location.href = "/login";
  } catch (err) {
    console.error("Logout failed:", err);
  }
};

const profileMenu = {
  items: [
    {
      key: "profile",
      label: "My Profile",
      onClick: () => navigate("/profile"),
    },
    {
      key: "company",
      label: "Company Settings",
      onClick: () => navigate("/settings"),
    },

    ...(user?.role === "admin"
      ? [
          {
            key: "admin",
            label: "Admin Panel",
            onClick: () => navigate("/admin"),
          },
        ]
      : []),

    { type: "divider" },

    {
      key: "logout",
      label: "Logout",
      danger: true,
      onClick: handleLogout,
    },
  ],
};

  const hotelMenu = {
    items: [
      {
        key: "upload",
        label: "Upload Hotels (CSV)",
        onClick: () => navigate("/hotels/upload"),
      },
      {
        key: "add",
        label: "Add Hotel",
        onClick: () => navigate("/hotels/new"),
      },
      {
        key: "view",
        label: "View Hotels",
        onClick: () => navigate("/hotels"),
      },
    ],
  };

const navItems = [
  { label: "Leads", path: "/leads" },

  ...( ["admin", "accounts", "reservations"].includes(user?.role)
      ? [{ label: "Bookings", path: "/bookings" }]
      : [] ),

...( ["admin", "sales_manager", "team_lead"].includes(user?.role)
      ? [{ label: "Reports", path: "/reports" }]
      : [] ),

  ...( ["admin", "accounts"].includes(user?.role)
      ? [{ label: "Accounts", path: "/accounts" }]
      : [] ),

  {
    label: "Transport",
    isTransportDropdown: true,
  },

  {
    label: "Hotels",
    isDropdown: true,
  },
];

  return (

<Header
  style={{
    background: "rgba(255, 255, 255, 0.92)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    borderBottom: "1px solid #e2e8f0",
    padding: isMobile ? "0 14px" : "0 24px",
    display: "flex",
    alignItems: "center",
    gap: isMobile ? 10 : 20,
    height: 64,
    overflow: "visible",
    position: "sticky",
    top: 0,
    zIndex: 1000,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.03)",
  }}
>

      {/* LEFT LOGO */}
<div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: isMobile ? "auto" : 140,
  }}
>
  <div
    onClick={() => navigate("/dashboard")}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      cursor: "pointer",
    }}
  >
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 10,
        background: "linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)",
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 800,
        fontSize: 15,
        boxShadow: "0 4px 12px rgba(79, 70, 229, 0.3)",
        letterSpacing: -0.5,
      }}
    >
      T
    </div>
    <div
      style={{
        fontWeight: 800,
        fontSize: 16,
        letterSpacing: -0.3,
        color: "#0f172a",
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      TRAVNOK
    </div>
  </div>
</div>

      {/* NAV */}
      <div
        style={{
          display: isMobile ? "none" : "flex",
          gap: 20,
          alignItems: "center",
        }}
      >
        {navItems.map((item) => {
          // 🔽 HOTEL DROPDOWN CASE
          if (item.isDropdown) {
            const active = location.pathname.startsWith("/hotels");
            return (
              <Dropdown key="hotels" menu={hotelMenu}>
                <div
                  style={{
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: active ? 700 : 500,
                    color: active ? "#4f46e5" : "#64748b",
                    paddingBottom: 4,
                    borderBottom: active ? "2.5px solid #4f46e5" : "2.5px solid transparent",
                    transition: "all 0.2s ease",
                  }}
                >
                  Hotels
                </div>
              </Dropdown>
            );
          }

          // ✅ NEW: TRANSPORT DROPDOWN (SAFE ADD)

if (item.isTransportDropdown) {
  const transportMenu = {
    items: [
      {
        key: "/operations",
        label: "Operations",
        onClick: () => navigate("/operations"),
      },
      {
        key: "/transport/inventory",
        label: "Transport Inventory",
        onClick: () => navigate("/transport/inventory"),
      },
    ],
  };

  const active =
    location.pathname.startsWith("/transport") ||
    location.pathname.startsWith("/operations");

  return (
    <Dropdown menu={transportMenu} placement="bottom">
      <div
        style={{
          cursor: "pointer",
          fontSize: 14,
          fontWeight: active ? 700 : 500,
          color: active ? "#4f46e5" : "#64748b",
          borderBottom: active
            ? "2.5px solid #4f46e5"
            : "2.5px solid transparent",
          paddingBottom: 4,
          display: "flex",
          alignItems: "center",
          transition: "all 0.2s ease",
        }}
      >
        Transport
      </div>
    </Dropdown>
  );
}

          // 🔽 NORMAL NAV ITEMS
          const active = location.pathname.startsWith(item.path);

          return (
            <div
              key={item.path}
              onClick={() => navigate(item.path)}
              style={{
                cursor: "pointer",
                fontSize: 14,
                fontWeight: active ? 700 : 500,
                color: active ? "#4f46e5" : "#64748b",
                borderBottom: active
                  ? "2.5px solid #4f46e5"
                  : "2.5px solid transparent",
                paddingBottom: 4,
                transition: "all 0.2s ease",
              }}
            >
              {item.label}
            </div>
          );
        })}
      </div>

      {/* GLOBAL SEARCH */}
      <div
        style={{
          flex: 1,
          maxWidth: isMobile ? "100%" : 460,
          marginLeft: isMobile ? 8 : 24,
          marginRight: isMobile ? 8 : 24,
          position: "relative",
        }}
      >
        <span
          style={{
            position: "absolute",
            left: 14,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 13,
            color: "#94a3b8",
            pointerEvents: "none",
          }}
        >
          🔍
        </span>

        <input
          type="text"
          placeholder="Search leads, trips, customer phone, email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => {
            if (results.trips.length > 0 || results.leads.length > 0) {
              setShowDropdown(true);
            }
          }}
          onBlur={() => {
            setTimeout(() => setShowDropdown(false), 200);
          }}
          style={{
            width: "100%",
            height: isMobile ? 38 : 38,
            padding: "0 14px 0 38px",
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            fontSize: 13,
            background: "#f8fafc",
            color: "#0f172a",
            outline: "none",
            transition: "all 0.2s ease",
            boxShadow: "inset 0 1px 2px rgba(0,0,0,0.02)",
          }}
        />

        {/* 🔽 DROPDOWN */}
        {showDropdown && (
          <div
            style={{
              position: "absolute",
              top: 38,
              left: 0,
              width: "100%",
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              zIndex: 20,
              maxHeight: 320,
              overflowY: "auto",
            }}
          >
            {/* TRIPS */}
            {results.trips.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#9ca3af",
                    padding: "8px 12px",
                  }}
                >
                  TRIPS
                </div>

                {results.trips.slice(0, 4).map((trip, index) => (
                  <div
                    key={trip._id}
                    onClick={() => navigate(`/trips/${trip._id}`)}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      background: index === activeIndex ? "#f3f4f6" : "#ffffff",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f9fafb";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#ffffff";
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {highlightMatch(
                        trip.customerName || "Unnamed",
                        searchQuery,
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      {trip.phone || ""} • {trip._id?.slice(-6)}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* LEADS */}
            {results.leads.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#9ca3af",
                    padding: "8px 12px",
                    borderTop: "1px solid #f3f4f6",
                  }}
                >
                  LEADS
                </div>

                {results.leads.slice(0, 4).map((lead, i) => {
                  const index = results.trips.length + i;

                  return (

<div
  key={lead._id}
  onClick={() => {
     navigate(`/trips/${lead._id}`);
    setShowDropdown(false);
  }}
  style={{
    padding: "8px 12px",
    cursor: "pointer",
    background:
      index === activeIndex ? "#f3f4f6" : "#ffffff",
  }}
>

                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {highlightMatch(lead.customerName  || "Unnamed", searchQuery)}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {lead.phone || ""}
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* EMPTY */}
            {results.trips.length === 0 && results.leads.length === 0 && (
              <div
                style={{
                  padding: 12,
                  fontSize: 13,
                  color: "#9ca3af",
                }}
              >
                No results found
              </div>
            )}
          </div>
        )}
      </div>

      {/* RIGHT ACTIONS */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 6 : 12,
        }}
      >
        <div
          onClick={() => setShowAdvancedFilters(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            height: 36,
            padding: "0 12px",
            borderRadius: 8,
            cursor: "pointer",
            color: activeFiltersCount > 0 ? "#4f46e5" : "#374151",
            background: activeFiltersCount > 0 ? "#eef2ff" : "transparent",
            border: activeFiltersCount > 0 ? "1px solid #c7d2fe" : "1px solid transparent",
            transition: "all 0.15s ease",
            position: "relative",
          }}
          onMouseEnter={(e) => {
            if (!activeFiltersCount) e.currentTarget.style.background = "#f3f4f6";
          }}
          onMouseLeave={(e) => {
            if (!activeFiltersCount) e.currentTarget.style.background = "transparent";
          }}
          title="Advanced Filters"
        >
          <FilterOutlined style={{ fontSize: 14, color: activeFiltersCount > 0 ? "#4f46e5" : "#64748b" }} />
          {!isMobile && (
            <span style={{ fontSize: 13, fontWeight: activeFiltersCount > 0 ? 600 : 500 }}>
              Filters
            </span>
          )}
          {activeFiltersCount > 0 && (
            <Badge
              count={activeFiltersCount}
              style={{
                backgroundColor: "#4f46e5",
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          )}
        </div>

        <NotificationBell />

        <Dropdown menu={profileMenu}>
          <Avatar
            size={isMobile ? 32 : "default"}
            icon={<UserOutlined />}
          />
        </Dropdown>

      </div>

      {/* ADVANCED FILTERS DRAWER PANEL */}
      <AdvancedFiltersPanel
        open={showAdvancedFilters}
        visible={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        onApply={handleApplyHeaderFilters}
        onReset={handleResetHeaderFilters}
        dashboardType="admin"
      />
    </Header>
  );
};

export default AdminHeader;
